import { useEffect } from 'react';
import Head from 'next/head';
import { gql } from '@apollo/client';
import { useRouter } from 'next/router';

import { client, getPostByURI } from '../../../apollo-client';
import { Pick } from '@components/pick/Pick';
import WideLayout from '@components/layout/wide';
import LoadingView from '@components/atoms/loading-view';
import {
  FplLinkGate,
  FplLinkGateProfileError
} from '@components/my-team/FplLinkGate';
import { useAuth0User } from '@hooks/useAuth0User';
import { useFplLinkGate } from '@hooks/useFplLinkGate';
import useLoggedOutRedirect from '@hooks/useLoggedOutRedirect';
import { getYoastHeadSEO, isPaidMember } from '@lib/utils';
import { pusherService } from '@lib/pusher';

const MyTeamPickPage = ({ post }) => {
  const { user, isLoading: userIsLoading } = useAuth0User();
  const router = useRouter();
  const { pathname } = router;
  const yoastHead = getYoastHeadSEO(post?.seo?.fullHead, pathname);

  useEffect(() => {
    pusherService.publishEvent('PAGE_VISIT_MY_TEAM_PICK', {
      page: 'my-team/pick'
    });
  }, []);

  useLoggedOutRedirect({
    router,
    redirectPath: 'ai-team-rating',
    user,
    userIsLoading
  });

  const isPayingMember = isPaidMember(user);
  const { status: linkGateStatus, refetchProfile } = useFplLinkGate();

  // The link gate applies to every signed-in user (free and paying):
  // post-GW1, pick needs a linked FPL team before the free-tier or member
  // experience is reachable. Logged-out visitors are redirected above.
  const renderPageContent = () => {
    if (linkGateStatus === 'loading') {
      return (
        <div className="flex justify-center py-10">
          <LoadingView
            headerText="Loading"
            loadingText="Getting your profile info"
            progressBarSeconds={8}
          />
        </div>
      );
    }
    if (linkGateStatus === 'error') {
      return <FplLinkGateProfileError onRetry={refetchProfile} />;
    }
    if (linkGateStatus === 'gate') {
      return <FplLinkGate />;
    }
    return <Pick isPayingMember={isPayingMember} />;
  };

  return (
    <>
      <Head>
        {yoastHead ?? (
          <title>
            My Team FPL - Use Artificial Intelligence To Win at Fantasy Football
          </title>
        )}
      </Head>
      <WideLayout className="bg-white" content={false}>
        <div className="relative px-1 pt-1 md:pt-3 pb-20 bg-gray-50 sm:px-6 lg:pb-10 lg:px-8">
          <div className="absolute inset-0">
            <div className="h-full bg-white" />
          </div>
          <div className="relative mx-auto max-w-7xl pb-14 min-h-screen">
            {!userIsLoading && renderPageContent()}
          </div>
        </div>
      </WideLayout>
    </>
  );
};

export const getStaticProps = async () => {
  const { data } = await client.query({
    query: gql`
      ${getPostByURI('/my-team-fpl-rate-my-team')}
    `
  });

  const post = data.postBy;

  return {
    props: { post }
  };
};

export default MyTeamPickPage;
